import React, { HTMLAttributes, useState, useEffect, useRef, useCallback } from 'react';
import '../../base.scss';
import { classNames } from '../../utilities';
import { Portal } from '../Portal';
import { CloseButton } from '../Button';
import useOutsideClick from '../../utilities/hooks/use-outside-click';
import { FocusLock } from '../FocusLock';
import './Modal.scss';

export interface ModalProps extends HTMLAttributes<HTMLElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /** Attach a custom class to the input container */
  className?: string;
  /** Is the modal closable? True for yes, false for no */
  closable?: boolean;
  /** Children to be displayed in the modal body */
  children?: string | string[] | React.ReactNode;
  /** Set to false if you do not want the modal open on render */
  defaultOpen?: boolean;
  /** Reference to element that opened up the modal for return focus*/
  finalFocusRef?: React.RefObject<HTMLElement>;
  /** function for closing the modal */
  onClose?: () => void;
  /** state management if the modal is open or not */
  isOpen?: boolean;
}

const Modal = (props: ModalProps) => {
  const { className, closable = true, children, onClose, isOpen, finalFocusRef } = props;

  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [showEntryAnimation, setShowEntryAnimation] = useState(isOpen);
  const modalRef = useRef<HTMLDivElement>(null);

  /** trigger the exit animation */
  const hideModal = useCallback(() => {
    setShowEntryAnimation(false);
  }, []);

  /** trigger the exit animation and close the modal */
  const closeModal = useCallback(() => {
    if (!showEntryAnimation) {
      if (onClose) {
        onClose();
      } else {
        setIsModalOpen(false);
      }
    }
  }, [showEntryAnimation, onClose]);

  /** sets the scroll prevention class on the document.body */
  useEffect(() => {
    isOpen !== undefined && setIsModalOpen(isOpen);
    if (isOpen) {
      modalRef?.current?.focus();
      document?.body?.classList.add('body--modal-mask', 'body--modal-open');
      setShowEntryAnimation(isOpen);
    } else {
      setShowEntryAnimation(isOpen);
      document?.body?.classList.remove('body--modal-mask', 'body--modal-open');
    }
  }, [isOpen]);

  /** keyboard listener for closing modal on escape */
  useEffect(() => {
    if (closable) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  });

  /** classname helpers */
  const modalClasses = classNames(
    'ps-modal',
    'aspect-base',
    showEntryAnimation ? 'ps-modal--open' : 'ps-modal--closed',
    className
  );
  const maskClasses = classNames(
    'ps-modal__mask',
    showEntryAnimation ? 'ps-modal__mask--open' : 'ps-modal__mask--closed',
    className
  );

  /** close button */
  const closeButton = closable ? <CloseButton className="ps-modal__close" onClick={hideModal} /> : null;

  /** close modal if user clicks off of modal */
  useOutsideClick(modalRef, () => closable && hideModal());

  /** handler for the escape key closing */
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      hideModal();
    }
  };

  if (isModalOpen) {
    return (
      <Portal id="ps-modal-id">
        <FocusLock finalFocusRef={finalFocusRef} autoFocus={false}>
          <div
            className={modalClasses}
            onAnimationEnd={closeModal}
            ref={modalRef}
            aria-label={props['aria-label']}
            data-autofocus
          >
            <div className="ps-modal__container">
              {closeButton}
              {children}
            </div>
          </div>
        </FocusLock>
        <div className={maskClasses} />
      </Portal>
    );
  } else {
    return null;
  }
};

Modal.displayName = 'Modal';
export { Modal };
